import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import TuneIcon from '@mui/icons-material/Tune'
import {
  alpha,
  Box,
  Button,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useMemo, useState } from 'react'

interface RoadmapRevisionFormProps {
  title: string
  onSubmit: (message: string) => void
  isInteractive: boolean
}

const ADJUSTMENT_TYPES = [
  { value: 'add-services', label: 'Add Services', desc: 'Include additional services or items' },
  { value: 'remove-services', label: 'Remove Services', desc: 'Take out specific services or items' },
  { value: 'swap-services', label: 'Swap Services', desc: 'Replace a service with another' },
  { value: 'adjust-budget', label: 'Change Budget', desc: 'Increase or decrease the budget' },
  { value: 'rebalance-phases', label: 'Rebalance Phases', desc: 'Redistribute items across phases' },
  { value: 'change-focus', label: 'Change Focus', desc: 'Shift emphasis between service lines' },
]

const SERVICE_LINES = [
  { value: 'seo', label: 'SEO', color: '#4AA17B' },
  { value: 'ppc', label: 'Paid Media', color: '#F59E0B' },
  { value: 'social', label: 'Social Media', color: '#8B5CF6' },
  { value: 'creative', label: 'Creative', color: '#EC4899' },
  { value: 'ana', label: 'Analytics', color: '#3498DB' },
  { value: 'emm', label: 'Email Marketing', color: '#E91E90' },
  { value: 'ux', label: 'UX/Design', color: '#DAA520' },
  { value: 'web', label: 'Web Dev', color: '#EF4444' },
  { value: 'int', label: 'Integration', color: '#06B6D4' },
]

const BUDGET_CHANGES = [
  { value: 'decrease-25', label: '-25%' },
  { value: 'decrease-10', label: '-10%' },
  { value: 'increase-10', label: '+10%' },
  { value: 'increase-25', label: '+25%' },
  { value: 'increase-50', label: '+50%' },
]

const PHASE_ACTIONS = [
  { value: 'front-load', label: 'Front-load', desc: 'More items in early phases' },
  { value: 'back-load', label: 'Back-load', desc: 'More items in later phases' },
  { value: 'even-out', label: 'Distribute Evenly', desc: 'Spread items more equally' },
  { value: 'add-phase', label: 'Add Phase', desc: 'Extend with an additional phase' },
  { value: 'remove-last', label: 'Remove Last Phase', desc: 'Shorten the engagement' },
]

const RoadmapRevisionForm: React.FC<RoadmapRevisionFormProps> = ({
  title,
  onSubmit,
  isInteractive,
}) => {
  const theme = useTheme()
  const [submitted, setSubmitted] = useState(false)
  const [adjustmentTypes, setAdjustmentTypes] = useState<string[]>([])

  // Load original config selections so user can see what's currently in the roadmap
  const originalConfig = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('roadmapOriginalConfig')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }, [])
  const currentServiceLines = new Set<string>(originalConfig?.serviceLines ?? [])

  // Contextual options
  const [addLines, setAddLines] = useState<string[]>([])
  const [removeLines, setRemoveLines] = useState<string[]>([])
  const [swapFrom, setSwapFrom] = useState<string[]>([])
  const [swapTo, setSwapTo] = useState<string[]>([])
  const [budgetChange, setBudgetChange] = useState('')
  const [phaseAction, setPhaseAction] = useState('')
  const [focusMore, setFocusMore] = useState<string[]>([])
  const [focusLess, setFocusLess] = useState<string[]>([])
  const [additionalNotes, setAdditionalNotes] = useState('')

  const handleAdjustmentToggle = (val: string) => {
    setAdjustmentTypes(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val],
    )
  }

  const handleChipToggle = (
    val: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setter(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val],
    )
  }

  const handleSubmit = () => {
    const parts: string[] = ['Revise the roadmap with these adjustments:']

    if (adjustmentTypes.includes('add-services') && addLines.length > 0) {
      const labels = addLines.map(v => SERVICE_LINES.find(s => s.value === v)?.label || v)
      parts.push(`Add more: ${labels.join(', ')}.`)
    }

    if (adjustmentTypes.includes('remove-services') && removeLines.length > 0) {
      const labels = removeLines.map(v => SERVICE_LINES.find(s => s.value === v)?.label || v)
      parts.push(`Remove or reduce: ${labels.join(', ')}.`)
    }

    if (adjustmentTypes.includes('swap-services') && swapFrom.length > 0 && swapTo.length > 0) {
      const fromLabels = swapFrom.map(v => SERVICE_LINES.find(s => s.value === v)?.label || v)
      const toLabels = swapTo.map(v => SERVICE_LINES.find(s => s.value === v)?.label || v)
      parts.push(`Swap out ${fromLabels.join(', ')} for ${toLabels.join(', ')}.`)
    }

    if (adjustmentTypes.includes('adjust-budget') && budgetChange) {
      const label = BUDGET_CHANGES.find(b => b.value === budgetChange)?.label || budgetChange
      parts.push(`Adjust budget: ${label}.`)
    }

    if (adjustmentTypes.includes('rebalance-phases') && phaseAction) {
      const action = PHASE_ACTIONS.find(p => p.value === phaseAction)
      parts.push(`Phase adjustment: ${action?.label || phaseAction} — ${action?.desc || ''}.`)
    }

    if (adjustmentTypes.includes('change-focus')) {
      if (focusMore.length > 0) {
        const labels = focusMore.map(v => SERVICE_LINES.find(s => s.value === v)?.label || v)
        parts.push(`Increase focus on: ${labels.join(', ')}.`)
      }
      if (focusLess.length > 0) {
        const labels = focusLess.map(v => SERVICE_LINES.find(s => s.value === v)?.label || v)
        parts.push(`Decrease focus on: ${labels.join(', ')}.`)
      }
    }

    if (additionalNotes.trim()) {
      parts.push(`Additional notes: ${additionalNotes.trim()}`)
    }

    setSubmitted(true)
    onSubmit(parts.join(' '))
  }

  const hasSelections = adjustmentTypes.length > 0

  if (!isInteractive || submitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          my: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 420,
          opacity: 0.8,
        }}
      >
        {submitted && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {submitted
            ? `Revision: ${adjustmentTypes
                .map(t => ADJUSTMENT_TYPES.find(a => a.value === t)?.label)
                .filter(Boolean)
                .join(', ')}`
            : title}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 1.5,
        py: 1.5,
        my: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        maxWidth: 460,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TuneIcon sx={{ fontSize: 16, color: theme.palette.text.ai_accent }} />
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          {title}
        </Typography>
      </Box>

      {/* Current Config Summary */}
      {originalConfig && (
        <Box sx={{ px: 1, py: 0.75, borderRadius: 1, bgcolor: alpha(theme.palette.text.ai_accent ?? '#A855F7', 0.06), border: '1px solid', borderColor: alpha(theme.palette.text.ai_accent ?? '#A855F7', 0.15) }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
            Current Roadmap
          </Typography>
          {originalConfig.serviceLines?.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.5 }}>
              {originalConfig.serviceLines.map((val: string) => {
                const sl = SERVICE_LINES.find(s => s.value === val)
                return sl ? (
                  <Chip
                    key={val}
                    label={sl.label}
                    size="small"
                    sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20, borderRadius: '6px', bgcolor: `${sl.color}18`, color: sl.color, border: '1px solid', borderColor: sl.color }}
                  />
                ) : null
              })}
            </Box>
          )}
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
            {originalConfig.clientName && `${originalConfig.clientName} · `}
            {originalConfig.duration}mo · ${originalConfig.budget?.toLocaleString()}/mo
          </Typography>
        </Box>
      )}

      {/* Adjustment Type Chips */}
      <Box>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
          What would you like to change?
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {ADJUSTMENT_TYPES.map(a => {
            const selected = adjustmentTypes.includes(a.value)
            return (
              <Chip
                key={a.value}
                label={a.label}
                size="small"
                onClick={() => handleAdjustmentToggle(a.value)}
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: selected ? theme.palette.text.ai_accent : 'divider',
                  bgcolor: selected ? alpha(theme.palette.text.ai_accent, 0.1) : 'transparent',
                  color: selected ? theme.palette.text.ai_accent : 'text.secondary',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(theme.palette.text.ai_accent, 0.15) },
                }}
              />
            )
          })}
        </Box>
      </Box>

      {/* Add Services */}
      {adjustmentTypes.includes('add-services') && (
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
            Add more from which service lines?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {SERVICE_LINES.map(s => {
              const selected = addLines.includes(s.value)
              return (
                <Chip
                  key={s.value}
                  label={s.label}
                  size="small"
                  onClick={() => handleChipToggle(s.value, setAddLines)}
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: selected ? s.color : 'divider',
                    bgcolor: selected ? `${s.color}18` : 'transparent',
                    color: selected ? s.color : 'text.secondary',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: `${s.color}28` },
                  }}
                />
              )
            })}
          </Box>
        </Box>
      )}

      {/* Remove Services */}
      {adjustmentTypes.includes('remove-services') && (
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
            Remove or reduce which service lines?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {SERVICE_LINES.map(s => {
              const selected = removeLines.includes(s.value)
              return (
                <Chip
                  key={s.value}
                  label={s.label}
                  size="small"
                  onClick={() => handleChipToggle(s.value, setRemoveLines)}
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: selected ? theme.palette.text.error : 'divider',
                    bgcolor: selected ? alpha(theme.palette.text.error, 0.1) : 'transparent',
                    color: selected ? theme.palette.text.error : 'text.secondary',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.text.error, 0.15) },
                  }}
                />
              )
            })}
          </Box>
        </Box>
      )}

      {/* Swap Services */}
      {adjustmentTypes.includes('swap-services') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
              Swap out:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {SERVICE_LINES.map(s => {
                const selected = swapFrom.includes(s.value)
                return (
                  <Chip
                    key={s.value}
                    label={s.label}
                    size="small"
                    onClick={() => handleChipToggle(s.value, setSwapFrom)}
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: selected ? theme.palette.text.error : 'divider',
                      bgcolor: selected ? alpha(theme.palette.text.error, 0.1) : 'transparent',
                      color: selected ? theme.palette.text.error : 'text.secondary',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: alpha(theme.palette.text.error, 0.15) },
                    }}
                  />
                )
              })}
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
              Replace with:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {SERVICE_LINES.map(s => {
                const selected = swapTo.includes(s.value)
                return (
                  <Chip
                    key={s.value}
                    label={s.label}
                    size="small"
                    onClick={() => handleChipToggle(s.value, setSwapTo)}
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: selected ? s.color : 'divider',
                      bgcolor: selected ? `${s.color}18` : 'transparent',
                      color: selected ? s.color : 'text.secondary',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: `${s.color}28` },
                    }}
                  />
                )
              })}
            </Box>
          </Box>
        </Box>
      )}

      {/* Budget Change */}
      {adjustmentTypes.includes('adjust-budget') && (
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
            Adjust budget by:
          </Typography>
          <ToggleButtonGroup
            value={budgetChange}
            exclusive
            onChange={(_, val) => val && setBudgetChange(val)}
            size="small"
            sx={{ display: 'flex', gap: 0.5 }}
          >
            {BUDGET_CHANGES.map(b => (
              <ToggleButton
                key={b.value}
                value={b.value}
                sx={{
                  flex: 1,
                  fontSize: '0.7rem',
                  py: 0.5,
                  textTransform: 'none',
                  borderRadius: '8px !important',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontWeight: 700,
                  color: b.value.startsWith('decrease') ? theme.palette.text.error : theme.palette.text.complete,
                }}
              >
                {b.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Phase Rebalancing */}
      {adjustmentTypes.includes('rebalance-phases') && (
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
            How should phases be adjusted?
          </Typography>
          <ToggleButtonGroup
            value={phaseAction}
            exclusive
            onChange={(_, val) => val && setPhaseAction(val)}
            size="small"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
          >
            {PHASE_ACTIONS.map(p => (
              <ToggleButton
                key={p.value}
                value={p.value}
                sx={{
                  flex: '1 0 auto',
                  fontSize: '0.6rem',
                  py: 0.5,
                  px: 1,
                  textTransform: 'none',
                  borderRadius: '8px !important',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ fontWeight: 700, fontSize: '0.65rem' }}>{p.label}</Box>
                  <Box sx={{ fontSize: '0.5rem', color: 'text.secondary' }}>{p.desc}</Box>
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Change Focus */}
      {adjustmentTypes.includes('change-focus') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
              Increase focus on:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {SERVICE_LINES.map(s => {
                const selected = focusMore.includes(s.value)
                return (
                  <Chip
                    key={s.value}
                    label={s.label}
                    size="small"
                    onClick={() => handleChipToggle(s.value, setFocusMore)}
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: selected ? s.color : 'divider',
                      bgcolor: selected ? `${s.color}18` : 'transparent',
                      color: selected ? s.color : 'text.secondary',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: `${s.color}28` },
                    }}
                  />
                )
              })}
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
              Decrease focus on:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {SERVICE_LINES.map(s => {
                const selected = focusLess.includes(s.value)
                return (
                  <Chip
                    key={s.value}
                    label={s.label}
                    size="small"
                    onClick={() => handleChipToggle(s.value, setFocusLess)}
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: selected ? theme.palette.text.error : 'divider',
                      bgcolor: selected ? alpha(theme.palette.text.error, 0.1) : 'transparent',
                      color: selected ? theme.palette.text.error : 'text.secondary',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: alpha(theme.palette.text.error, 0.15) },
                    }}
                  />
                )
              })}
            </Box>
          </Box>
        </Box>
      )}

      {/* Additional Notes */}
      {hasSelections && (
        <TextField
          size="small"
          label="Additional notes (optional)"
          placeholder="Any specific items to add/remove, phase preferences..."
          value={additionalNotes}
          onChange={e => setAdditionalNotes(e.target.value)}
          multiline
          maxRows={2}
          sx={{
            '& .MuiInputBase-root': { fontSize: '0.8rem' },
            '& .MuiInputLabel-root': { fontSize: '0.8rem' },
          }}
        />
      )}

      {/* Submit */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleSubmit}
          disabled={!hasSelections}
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            borderColor: 'text.ai_agent_text',
            color: 'text.ai_agent_text',
            '&:hover': { borderColor: 'text.highlight', color: 'text.primary' },
          }}
        >
          Apply Revisions
        </Button>
      </Box>
    </Box>
  )
}

export default RoadmapRevisionForm
